<template>
    <div class="contact-notes">
        <div class="notes">
            <ContactNoteItem
                v-for="note in notes"
                :key="note._id"
                :note="note"
                :visitor-id="visitorId"
                @note-updated="noteUpdated"
                @note-deleted="noteDeleted"
            />
        </div>
        <div class="fixed-bottom-area">
            <div class="fixed-bottom-area-header">
                <div class="material-icons">draw</div>
                <span class="fixed-bottom-area-title">{{ $t("message.addNote") }}</span>
            </div>
            <div class="fixed-bottom-area-content">
                <textarea
                    v-model="description"
                    :placeholder="$t('message.addUserNote')"
                    class="non-resizable"
                ></textarea>
            </div>
            <div class="fixed-bottom-area-footer">
                <div class="contact-notes-form-actions">
                    <a-checkbox v-model="isPrivate">{{ $t("message.privateNote") }}</a-checkbox>
                    <div class="contact-notes-form-actions-buttons">
                        <div class="custom-submit-button" @click="submitForm">
                            <span>{{ $t("message.send") }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import AgentAvatar from "../../common/AgentAvatar.vue";
import ContactNoteItem from "./ContactNoteItem.vue";
import { sendSocketMessage } from "../../../utils/socketStream";

export default {
    name: "ContactNotes",
    components: { ContactNoteItem, AgentAvatar, SupsisButton },
    props: {
        visitorId: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        notes: [],
        isPrivate: false,
        description: null,
        updatingNote: null,
        isExpanded: false,
    }),
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
        }),
    },
    methods: {
        ...mapActions({
            createNote: "visitor/createVisitorNote",
            updateNote: "visitor/updateVisitorNote",
            getNotes: "visitor/getVisitorNotes",
            deleteVisitorNote: "visitor/deleteVisitorNote",
            updateContactFields: "contact/updateContactFields",
        }),
        noteUpdated(note) {
            this.notes = this.notes
                .map((n) => (n._id === note._id ? note : n))
                .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                .sort((a, b) => b.isPrivate - a.isPrivate);
        },
        noteDeleted(noteId) {
            const note = this.notes.find((n) => n._id === noteId);
            this.notes = this.notes.filter((n) => n._id !== noteId);
            if (!note.isPrivate) {
                sendSocketMessage("visitor note-deleted", {
                    siteId: this.site._id,
                    contactId: this.visitorId,
                });
            } else {
                this.updateContactFields({
                    contactId: this.visitorId,
                    set: {
                        noteCount: this.notes.length,
                    },
                });
            }
        },
        submitForm() {
            this.createNote({
                visitorId: this.visitorId,
                payload: {
                    isPrivate: this.isPrivate,
                    description: this.description,
                },
            }).then((body) => {
                this.notes = [body, ...this.notes]
                    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                    .sort((a, b) => b.isPrivate - a.isPrivate);

                if (!this.isPrivate) {
                    sendSocketMessage("visitor note-added", {
                        siteId: this.site._id,
                        contactId: this.visitorId,
                    });
                } else {
                    this.updateContactFields({
                        contactId: this.visitorId,
                        set: {
                            noteCount: this.notes.length,
                        },
                    });
                }

                this.isPrivate = false;
                this.description = null;
            });
        },
        init() {
            this.getNotes(this.visitorId).then((notes) => {
                this.notes = notes;
            });
        },
    },
    mounted() {
        this.init();
    },
    watch: {
        visitorId(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.init();
            }
        },
    },
};
</script>
<style lang="scss">
.note-button {
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}
.note-delete-button {
    width: 30%;
    background-color: #ff5050;
    color: white;
}
.note-update-button {
    width: 60%;
    background-color: #f1f4f7;
    color: #647491;
}
.contact-notes-update-buttons {
    width: 95%;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    align-items: center;
    height: 30px;
    cursor: pointer;
    .note-update-ok {
        height: 100%;
        background-color: #22272d;
        color: white;
        width: 30%;
        margin-right: 15px;
    }
    .note-update-cancel {
        height: 100%;
        background-color: #f1f4f7;
        color: #647491;
        width: 30%;
    }
}
.contact-note-popover {
    .ant-popover-arrow {
        display: none !important;
    }

    .ant-popover-inner-content {
        box-shadow: none !important;
        border: 1px solid var(--border-neutral-default);
        border-radius: 4px;
        padding: 0 !important;

        .more-actions {
            overflow: hidden;

            :not(:last-child).action-button {
                border-bottom: 1px solid var(--border-neutral-default);
            }

            .action-button {
                display: flex;
                align-items: center;
                padding: 4px 10px;
                gap: 4px;
                cursor: pointer;

                &:hover {
                    background: #22272d;
                    border-radius: 4px;

                    .action-button-icon {
                        .material-icons {
                            color: white;
                        }
                    }

                    .action-button-text {
                        color: white;
                    }
                }

                &-icon {
                    line-height: 0;

                    .material-icons {
                        font-size: 18px;
                    }
                }

                &-text {
                    font-size: 16px;
                }
            }
        }
    }
}
.contact-notes {
    display: flex;
    flex-direction: column;
    gap: 30px;
    height: 100%;
    align-items: center;
    .user-info-content-title {
        width: 100%;
        padding: 10px 10px 0 10px;
        display: flex;
        justify-content: space-between;
    }
    .fixed-bottom-area {
        position: relative;
        border-top: 1px solid #f1f4f7;
        bottom: 0;
        left: 0;
        width: 100%;
        background: #ffffff;
        padding: 10px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        cursor: pointer;
        .fixed-bottom-area-footer {
            display: flex;
            justify-content: space-between;
            width: 100%;
            align-items: center;
            .custom-submit-button {
                background: black;
                width: 60px;
                height: 30px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                cursor: pointer;
                span {
                    color: white;
                    font-size: 14px;
                }
            }
        }
        .fixed-bottom-area-content {
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 10px;
        }
        textarea {
            width: 100%;
            padding: 5px;
            border: 1px solid #ccc;
            border-radius: 4px;
            resize: none;
            background-color: #f1f4f7;
        }
        .fixed-bottom-area-header {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 20px;
            width: 100%;

            .material-icons {
                font-size: 20px;
                fill: transparent !important;
            }
            .fixed-bottom-area-title {
                font-size: 16px;
                font-weight: bold;
            }
        }
    }

    &-form {
        display: flex;
        flex-direction: column;
        gap: 4px;

        textarea.ant-input:hover,
        textarea.ant-input:focus {
            border-color: #22272d !important;
            box-shadow: none !important;
        }

        &-actions {
            display: flex;
            justify-content: space-between;
            width: 100%;

            .ant-checkbox-wrapper:hover .ant-checkbox-inner,
            .ant-checkbox:hover .ant-checkbox-inner,
            .ant-checkbox-input:focus + .ant-checkbox-inner,
            .ant-checkbox-checked .ant-checkbox-inner,
            .ant-checkbox-checked::after {
                border-color: #22272d !important;
            }

            .ant-checkbox-checked .ant-checkbox-inner {
                background: #22272d !important;
            }

            &-buttons {
                display: flex;
                align-items: center;
                gap: 4px;

                &-item {
                    background: #22272d;
                    width: 25px;
                    height: 25px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 3px;
                    line-height: 0;
                    cursor: pointer;

                    .material-icons {
                        user-select: none;
                        color: white;
                        font-size: 18px;
                    }
                }
            }
        }
    }

    .notes {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
        overflow: auto;
        padding: 0 10px;
        &::-webkit-scrollbar {
            height: 0;
            width: 0;
            display: none;
        }
        &:hover {
            &::-webkit-scrollbar {
                width: 3px;
                display: block;
            }
        }

        .contact-note {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            padding: 8px;

            &.private-note {
                background-color: rgb(253, 255, 104); // todo set to attribute
            }
            .contact-note-content {
                display: flex;
                flex-direction: column;
                gap: 5px;
                width: 100%;
                .contact-note-details-date {
                    font-size: 12px;
                    color: #647491;
                }
            }
            .note-action-button {
                display: flex;
                justify-content: flex-start;
                width: 100%;
                gap: 8px;
                height: 30px;
            }
            .note-update-cancel {
            }
            .note-update-ok {
            }

            &-agent-avatar {
                width: 30px;
                height: 30px;
                border-radius: 30px;
            }

            &-details {
                display: flex;
                flex-direction: column;
                gap: 3px;
                width: 100%;

                &-title {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;

                    .contact-note-agent-name {
                        font-size: 16px;
                        display: flex;
                        align-items: center;
                        font-weight: bold;
                        gap: 4px;
                        .material-icons {
                            font-size: 16px;
                            line-height: 0;
                            user-select: none;
                        }
                    }
                    .contact-note-more-actions {
                        .material-icons {
                            cursor: pointer;
                            width: 20px;
                            height: 20px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 16px;
                        }
                    }
                }

                &-description {
                    border-radius: 4px;
                    //max-height: 60px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    -webkit-line-clamp: 2;
                    line-clamp: 2;
                    -webkit-box-orient: vertical;
                }
            }
        }
    }
}
.non-resizable {
    resize: none !important;
}
</style>
